import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Container, Card, Button, Alert, Form, Modal, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { FiPlus, FiHome, FiEye, FiEdit2, FiFileText, FiCheckSquare } from 'react-icons/fi';
import ChurchTemplatesModal from '@features/church/components/ChurchTemplatesModal';
import BulkAssignTemplatesModal from '@features/church/components/BulkAssignTemplatesModal';
import TableActionButton from '@core/components/TableActionButton';
import TableActions from '@core/components/TableActions';
import { SearchBar, FilterField } from '@core/components/SearchBar';
import { useChurches } from '@features/church/hooks/useChurches';
import { createChurch, updateChurch } from '@features/church/api/churchApi';
import { DataTable, DataTableColumn } from '@core/components/DataTable';
import { Church } from '@features/church/types/church';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useErrorModalStore } from '@infrastructure/api/store/errorModalStore';
import { ErrorResponse } from '@core/types/errorResponse';
import { useDebounce } from '@core/hooks/useDebounce';
import { useSearchUrlSync } from '@core/hooks/useSearchUrlSync';
import {
  NAME_SORT_FILTER_FIELD,
  SORT_BY_CHURCH_NAME,
  SORT_DIR_ASC,
  countContentFilters,
  filtersWithoutSort,
  normalizeSortDir,
  readSortDirFromSearchParams,
  withCatalogSort,
} from '@core/constants/listSort';
import { buildDefaultListParams } from '@core/utils/listQuery';
import { usePermissions, useEntityContext } from '@features/entity/hooks/usePermissions';
import { ACTION_CODES, BULK_TEMPLATE_ASSIGN_CODES } from '@features/entity/types/actionCodes';
import { useBulkSelection } from '@core/hooks/useBulkSelection';
import ChurchEditorTabs from '@features/membership/components/ChurchEditorTabs';
import ChurchMembershipTab from '@features/membership/components/ChurchMembershipTab';
import HeadChurchLabel from '@features/district/components/HeadChurchLabel';
import SetHeadChurchModal from '@features/district/components/SetHeadChurchModal';
import { fetchDistrictById } from '@features/district/api/districtApi';

const CHURCH_FILTER_FIELDS: FilterField[] = [
  NAME_SORT_FILTER_FIELD,
  { name: 'churchName', label: 'Church Name', type: 'text', placeholder: 'Filter by church name', colWidth: 6 },
];

const DISTRICT_CHURCH_LIST_CRITERIA_KEYS = ['freetext', 'churchName', 'districtId'] as const;

const DistrictChurchesPage: React.FC = () => {
  const location = useLocation();
  const { districtId } = useParams<{ districtId: string }>();
  const [districtName, setDistrictName] = useState<string | null>(
    (location.state?.districtName as string | undefined)?.trim() || null
  );
  const showValidation = useErrorModalStore((s) => s.showValidation);
  const displayDistrictName = districtName || '…';

  // ─── Permissions ────────────────────────────────────────────────────
  const { context } = useEntityContext();
  const { hasPermission, hasAnyPermission } = usePermissions({
    scopeType: context?.scope,
    scopeId: context?.scopeId,
    actionCodes: [
      ACTION_CODES.CREATE_CHURCH,
      ACTION_CODES.UPDATE_CHURCH,
      ACTION_CODES.SET_DISTRICT_HEAD_CHURCH,
      ACTION_CODES.UPDATE_CHURCH_MEMBERSHIP,
      ACTION_CODES.OVERRIDE_CHURCH_MEMBERSHIP_COUNT,
      ACTION_CODES.SYNC_CHURCH_REPORT_TEMPLATE,
      ACTION_CODES.BULK_ASSIGN_OVERRIDE_CHURCH_REPORT_TEMPLATE,
      ...BULK_TEMPLATE_ASSIGN_CODES,
    ],
  });
  const canCreate = hasPermission(ACTION_CODES.CREATE_CHURCH);
  const canSetHeadChurch = hasPermission(ACTION_CODES.SET_DISTRICT_HEAD_CHURCH);
  const canEdit = hasPermission(ACTION_CODES.UPDATE_CHURCH);
  const [showHeadChurchModal, setShowHeadChurchModal] = useState(false);
  const [headChurchIdFromDistrict, setHeadChurchIdFromDistrict] = useState<number | null>(null);
  const canUpdateMembership = hasPermission(ACTION_CODES.UPDATE_CHURCH_MEMBERSHIP);
  const canOverrideMembership = hasPermission(ACTION_CODES.OVERRIDE_CHURCH_MEMBERSHIP_COUNT);
  const canViewMembership = canUpdateMembership || canOverrideMembership;
  const canManageTemplates = hasPermission(ACTION_CODES.SYNC_CHURCH_REPORT_TEMPLATE);
  const canBulkAssign = hasAnyPermission(BULK_TEMPLATE_ASSIGN_CODES as unknown as string[]);

  // ─── Bulk Selection ────────────────────────────────────────────────
  const bulkSelection = useBulkSelection<Church>();
  const [showBulkModal, setShowBulkModal] = useState(false);

  const nationId = useMemo(() => {
    if (context?.scope === 'NATION') return context.scopeId;
    return 0;
  }, [context]);

  // ─── URL-synced search ──────────────────────────────────────────────
  const [urlParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => urlParams.get('q') ?? '');
  const debouncedQuery = useDebounce(searchQuery, 300);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
    const result: Record<string, any> = {};
    urlParams.forEach((v, k) => { if (k !== 'q' && k !== 'page') result[k] = v; });
    result.sortDir = readSortDirFromSearchParams(urlParams);
    return result;
  });
  useSearchUrlSync({ query: debouncedQuery, filters: activeFilters, page: 1 });
  const previousPathnameRef = useRef(location.pathname);

  // View/Edit modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewChurch, setViewChurch] = useState<Church | null>(null);
  const [formData, setFormData] = useState<Partial<Church>>({});
  const [formLoading, setFormLoading] = useState(false);

  // Create church modal state (opens in one click with district prefilled)
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createFormData, setCreateFormData] = useState<Partial<Church>>({});
  const [createLoading, setCreateLoading] = useState(false);
  const [createFormErrors, setCreateFormErrors] = useState<Partial<Record<string, string[]>>>({});
  const [editFormErrors, setEditFormErrors] = useState<Partial<Record<string, string[]>>>({});
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [selectedChurch, setSelectedChurch] = useState<Church | null>(null);

  const {
    churches,
    loading,
    error,
    pagination,
    refetch,
    updateParams,
    validationErrors
  } = useChurches(
    withCatalogSort(
      {
        districtId: districtId ? parseInt(districtId) : undefined,
        perPage: 20,
        page: 1,
        ...(searchQuery ? { freetext: searchQuery } : {}),
        ...filtersWithoutSort(activeFilters),
        sortDir: normalizeSortDir(activeFilters.sortDir),
      },
      SORT_BY_CHURCH_NAME
    )
  );

  const selectedChurchesForBulk = useMemo(() =>
    bulkSelection.getSelectedItems(churches, (c) => c.church_id).map((c) => ({
      id: c.church_id,
      name: c.church_name,
    })),
    [bulkSelection, churches]
  );

  const currentHeadChurchId = useMemo(() => {
    const fromList = churches.find((c) => c.is_head_church)?.church_id;
    return fromList ?? headChurchIdFromDistrict;
  }, [churches, headChurchIdFromDistrict]);

  useEffect(() => {
    const id = districtId ? parseInt(districtId, 10) : 0;
    if (!id) return;
    let cancelled = false;
    fetchDistrictById(id).then((response) => {
      if (cancelled || !response.isSuccess()) return;
      const district = response.getData();
      const name = district?.district_name?.trim();
      if (name) setDistrictName(name);
      setHeadChurchIdFromDistrict(district?.head_church_id ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [districtId]);

  // Push debounced query + filters to the data hook whenever they change
  useEffect(() => {
    updateParams(
      withCatalogSort(
        {
          freetext: debouncedQuery || undefined,
          ...filtersWithoutSort(activeFilters),
          sortDir: normalizeSortDir(activeFilters.sortDir),
        },
        SORT_BY_CHURCH_NAME
      )
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, activeFilters]);

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return;

    previousPathnameRef.current = location.pathname;
    setSearchQuery('');
    setActiveFilters({ sortDir: SORT_DIR_ASC });
    const numericId = districtId ? parseInt(districtId, 10) : undefined;
    const next = withCatalogSort(
      buildDefaultListParams(
        {
          districtId: numericId,
          page: 1,
          freetext: undefined,
          churchName: undefined,
          sortDir: SORT_DIR_ASC,
        },
        [...DISTRICT_CHURCH_LIST_CRITERIA_KEYS],
      ),
      SORT_BY_CHURCH_NAME
    );
    updateParams(next);
  }, [districtId, location.pathname, updateParams]);

  const handleOpenCreateChurch = () => {
    setCreateFormErrors({});
    const numericDistrictId = districtId ? parseInt(districtId, 10) : undefined;
    setCreateFormData({
      church_name: '',
      description: '',
      address: '',
      phone: '',
      email: '',
      pastor: '',
      status: 'active',
      district_id: numericDistrictId,
    });
    setShowCreateModal(true);
  };

  const handleCreateChurchSubmit = async () => {
    const messages: string[] = [];
    const errors: Partial<Record<string, string[]>> = {};
    if (!createFormData.church_name?.trim()) {
      messages.push('Church name is required.');
      errors.church_name = ['Church name is required.'];
    }
    if (!createFormData.district_id) {
      messages.push('District is required.');
      errors.district_id = ['District is required.'];
    }
    if (messages.length > 0) {
      setCreateFormErrors(errors);
      showValidation(messages);
      return;
    }
    setCreateLoading(true);
    setCreateFormErrors({});
    try {
      const response = await createChurch(createFormData.district_id as number, {
        name: createFormData.church_name as string,
        description: createFormData.description || '',
        alias: (createFormData as any).alias || undefined,
        established_at: createFormData.established_date || undefined,
      });
      if (response.isSuccess()) {
        setShowCreateModal(false);
        refetch();
      } else {
        const err = response.getError() as ErrorResponse | undefined;
        const raw = (err?.errors ?? {}) as Record<string, string[]>;
        const fieldErrors: Partial<Record<string, string[]>> = { ...raw };
        if (raw.name) fieldErrors.church_name = raw.name;
        setCreateFormErrors(fieldErrors);
        const list = Object.values(fieldErrors).flat().filter((m): m is string => Boolean(m));
        showValidation(list.length ? list : [err?.detail || 'Failed to create church']);
      }
    } catch (err) {
      console.error('Error creating church:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleViewChurch = (church: Church) => {
    setEditFormErrors({});
    setViewChurch(church);
    setFormData({ ...church });
    setShowViewModal(true);
  };

  const handleUpdateChurch = async () => {
    if (!viewChurch) return;
    const messages: string[] = [];
    const errors: Partial<Record<string, string[]>> = {};
    if (!formData.church_name?.trim()) {
      messages.push('Church name is required.');
      errors.church_name = ['Church name is required.'];
    }
    if (messages.length > 0) {
      setEditFormErrors(errors);
      showValidation(messages);
      return;
    }
    setFormLoading(true);
    setEditFormErrors({});
    try {
      const response = await updateChurch(viewChurch.district_id, viewChurch.church_id, {
        name: formData.church_name ?? '',
        description: formData.description || '',
        alias: (formData as any).alias || undefined,
        established_at: (formData as any).established_at || undefined,
      });
      if (response.isSuccess()) {
        setShowViewModal(false);
        refetch();
      } else {
        const error = response.getError() as ErrorResponse | undefined;
        const raw = (error?.errors ?? {}) as Record<string, string[]>;
        const fieldErrors: Partial<Record<string, string[]>> = { ...raw };
        if (raw.name) fieldErrors.church_name = raw.name;
        setEditFormErrors(fieldErrors);
        const list = Object.values(fieldErrors).flat().filter((m): m is string => Boolean(m));
        showValidation(list.length ? list : [error?.detail || 'Failed to update church']);
      }
    } catch (error) {
      console.error('Error updating church:', error);
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenTemplates = (church: Church) => {
    setSelectedChurch(church);
    setShowTemplatesModal(true);
  };

  const numericDistrictId = districtId ? parseInt(districtId, 10) : 0;

  // Table columns configuration
  const columns: DataTableColumn<Church>[] = [
    ...(canBulkAssign ? [{
      key: 'select',
      label: (
        <Form.Check
          type="checkbox"
          checked={churches.length > 0 && churches.every((c) => bulkSelection.isSelected(c.church_id))}
          onChange={() => bulkSelection.toggleAll(churches, (c) => c.church_id)}
          aria-label="Select all churches"
        />
      ) as any,
      width: '40px',
      render: (_value: any, row: Church) => (
        <Form.Check
          type="checkbox"
          checked={bulkSelection.isSelected(row.church_id)}
          onChange={(e) => {
            e.stopPropagation();
            bulkSelection.toggleItem(row.church_id);
          }}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Select ${row.church_name}`}
        />
      ),
    }] : []),
    {
      key: 'church_name',
      label: 'Church Name',
      role: 'title',
      render: (_value: any, row: Church) => (
        <div className="d-flex align-items-center">
          <FiHome className="me-2 text-muted" />
          <div>
            <div className="fw-medium">
              {row.church_name}
              {row.is_head_church ? <HeadChurchLabel /> : null}
            </div>
            {row.description && (
              <small className="text-muted">{row.description}</small>
            )}
          </div>
        </div>
      ),
      cardRender: (_value: any, row: Church) => (
        <>
          {row.church_name}
          {row.is_head_church ? <HeadChurchLabel /> : null}
        </>
      ),
    },
    {
      key: 'description',
      label: 'Description',
      role: 'description',
      tableHidden: true,
      render: (_value: any, row: Church) => row.description || null,
    },
    {
      key: 'actions',
      label: 'Actions',
      role: 'actions',
      render: (_value: any, row: Church) => (
        <TableActions aria-label={`Actions for church ${row.church_name}`}>
          <TableActionButton
            icon={canEdit ? <FiEdit2 /> : <FiEye />}
            label={canEdit ? 'Edit' : 'View'}
            primary
            variant="outline-primary"
            onClick={() => handleViewChurch(row)}
          />
          <TableActionButton
            icon={canManageTemplates ? <FiFileText /> : <FiEye />}
            label={canManageTemplates ? 'Manage Templates' : 'View Templates'}
            primary
            variant="outline-secondary"
            onClick={() => handleOpenTemplates(row)}
          />
        </TableActions>
      )
    }
  ];

  return (
    <Container fluid className="py-4">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-md-center gap-3 mb-4">
        <div className="ntcog-page-title-block mb-0">
          <h1 className="ntcog-page-heading mb-1">{displayDistrictName} - Churches</h1>
          <p className="text-muted mb-0">Manage churches in {displayDistrictName}</p>
        </div>
        {(canSetHeadChurch || canCreate) && (
          <div className="d-flex flex-column flex-sm-row align-items-stretch align-items-sm-center gap-2">
            {canSetHeadChurch && (
              <Button
                variant="link"
                className="ntcog-muted-link px-0"
                onClick={() => setShowHeadChurchModal(true)}
              >
                Set head church
              </Button>
            )}
            {canCreate && (
              <Button variant="primary" onClick={handleOpenCreateChurch}>
                <FiPlus className="me-1" />
                Create Church
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Search Bar */}
      <div className="ntcog-list-search-section">
        <SearchBar
          placeholder="Search churches…"
          filterFields={CHURCH_FILTER_FIELDS}
          initialQuery={searchQuery}
          initialFilters={activeFilters}
          onQueryChange={setSearchQuery}
          onFiltersApply={(f) => {
            setActiveFilters({ ...f, sortDir: normalizeSortDir(f.sortDir) });
          }}
          onClear={() => {
            setSearchQuery('');
            setActiveFilters({ sortDir: SORT_DIR_ASC });
            updateParams(
              withCatalogSort(
                {
                  freetext: undefined,
                  churchName: undefined,
                  sortDir: SORT_DIR_ASC,
                },
                SORT_BY_CHURCH_NAME
              )
            );
          }}
          loading={loading}
          activeFilterCount={countContentFilters(activeFilters)}
          testId="district-churches-search"
        />
      </div>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <Alert variant="warning" className="mb-4">
          <strong>Validation Errors:</strong>
          <ul className="mb-0 mt-2">
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Data Table */}
      <Card className="shadow-sm ntcog-card ntcog-list-table-card">
        <Card.Body className="p-0">
          <DataTable
              data={churches}
              columns={columns}
              rowNumberOffset={(pagination?.from ?? 1) - 1}
              loading={loading}
              error={error}
              emptyMessage="No churches found"
              cardAriaLabel={(row) => `Church ${row.church_name}`}
              striped={true}
              hover={true}
              responsive={true}
            />
        </Card.Body>
      </Card>

      {/* Create Church Modal - one click opens with district prefilled */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Church</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>District</Form.Label>
              <Form.Control type="text" value={displayDistrictName} readOnly className="bg-light" />
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Church Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={createFormData.church_name || ''}
                    onChange={(e) => {
                      setCreateFormData({ ...createFormData, church_name: e.target.value });
                      if (createFormErrors.church_name) setCreateFormErrors((prev) => ({ ...prev, church_name: undefined }));
                    }}
                    required
                    isInvalid={!!createFormErrors.church_name}
                  />
                  <Form.Control.Feedback type="invalid">{createFormErrors.church_name?.[0]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Alias</Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={(createFormData as any).alias || ''}
                    onChange={(e) => setCreateFormData({ ...createFormData, alias: e.target.value } as any)}
                    placeholder="e.g. WPC"
                    isInvalid={!!createFormErrors.alias}
                  />
                  <Form.Control.Feedback type="invalid">{createFormErrors.alias?.[0]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={255}
                value={createFormData.description || ''}
                onChange={(e) => {
                  setCreateFormData({ ...createFormData, description: e.target.value });
                  if (createFormErrors.description) setCreateFormErrors((prev) => ({ ...prev, description: undefined }));
                }}
                isInvalid={!!createFormErrors.description}
              />
              <Form.Control.Feedback type="invalid">{createFormErrors.description?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Established Date</Form.Label>
              <Form.Control
                type="date"
                value={createFormData.established_date || ''}
                onChange={(e) => setCreateFormData({ ...createFormData, established_date: e.target.value })}
              />
            </Form.Group>
          </Form>
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="primary"
              onClick={handleCreateChurchSubmit}
              disabled={createLoading}
              aria-busy={createLoading}
            >
              {createLoading ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <FiPlus className="me-2" />
                  Create Church
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* View/Edit Church Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{canEdit ? 'Edit Church' : 'Church Details'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ChurchEditorTabs
            showMembership={canViewMembership && viewChurch != null}
            details={(
          <>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Church Name <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                value={formData.church_name || ''}
                onChange={(e) => {
                  setFormData({ ...formData, church_name: e.target.value });
                  if (editFormErrors.church_name) setEditFormErrors((prev) => ({ ...prev, church_name: undefined }));
                }}
                required
                disabled={!canEdit}
                isInvalid={!!editFormErrors.church_name}
              />
              <Form.Control.Feedback type="invalid">{editFormErrors.church_name?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={255}
                value={formData.description || ''}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  if (editFormErrors.description) setEditFormErrors((prev) => ({ ...prev, description: undefined }));
                }}
                disabled={!canEdit}
                isInvalid={!!editFormErrors.description}
              />
              <Form.Control.Feedback type="invalid">{editFormErrors.description?.[0]}</Form.Control.Feedback>
            </Form.Group>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Alias</Form.Label>
                  <Form.Control
                    type="text"
                    maxLength={100}
                    value={(formData as any).alias || ''}
                    onChange={(e) => setFormData({ ...formData, alias: e.target.value } as any)}
                    placeholder="e.g. WPC"
                    disabled={!canEdit}
                    isInvalid={!!editFormErrors.alias}
                  />
                  <Form.Control.Feedback type="invalid">{editFormErrors.alias?.[0]}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Established Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={(formData as any).established_at || ''}
                    onChange={(e) => setFormData({ ...formData, established_at: e.target.value } as any)}
                    disabled={!canEdit}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
          {canEdit && (
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                onClick={handleUpdateChurch}
                disabled={formLoading}
                aria-busy={formLoading}
              >
                {formLoading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  'Update Church'
                )}
              </Button>
            </div>
          )}
          </>
            )}
            membership={viewChurch ? (
              <ChurchMembershipTab
                apiPath={`district/${numericDistrictId}/church/${viewChurch.church_id}`}
                canUpdate={canUpdateMembership}
                canOverride={canOverrideMembership}
              />
            ) : null}
          />
        </Modal.Body>
      </Modal>

      <ChurchTemplatesModal
        show={showTemplatesModal}
        onHide={() => setShowTemplatesModal(false)}
        church={selectedChurch}
        districtId={selectedChurch?.district_id ?? numericDistrictId}
        canManage={canManageTemplates}
      />

      {canBulkAssign && (
        <BulkAssignTemplatesModal
          show={showBulkModal}
          onHide={() => setShowBulkModal(false)}
          entities={selectedChurchesForBulk}
          entityType="church"
          nationId={nationId}
          districtId={numericDistrictId}
          onCompleted={() => {
            bulkSelection.deselectAll();
            refetch();
          }}
        />
      )}

      {canBulkAssign && bulkSelection.selectedCount > 0 && (
        <div
          className="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-3 bg-white shadow-lg rounded d-flex align-items-center gap-3"
          style={{ zIndex: 1050 }}
        >
          <Badge bg="primary">{bulkSelection.selectedCount} selected</Badge>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowBulkModal(true)}
          >
            <FiCheckSquare className="me-2" />
            Bulk Assign Templates
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => bulkSelection.deselectAll()}
          >
            Clear Selection
          </Button>
        </div>
      )}

      {canSetHeadChurch && numericDistrictId > 0 && (
        <SetHeadChurchModal
          show={showHeadChurchModal}
          districtId={numericDistrictId}
          districtName={displayDistrictName}
          currentHeadChurchId={currentHeadChurchId}
          onHide={() => setShowHeadChurchModal(false)}
          onSaved={() => {
            refetch();
            fetchDistrictById(numericDistrictId).then((response) => {
              if (!response.isSuccess()) return;
              const district = response.getData();
              const name = district?.district_name?.trim();
              if (name) setDistrictName(name);
              setHeadChurchIdFromDistrict(district?.head_church_id ?? null);
            });
          }}
        />
      )}
    </Container>
  );
};

export default DistrictChurchesPage;
